import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPinIcon, CalendarIcon, BeakerIcon, BuildingOfficeIcon, UserIcon, PhoneIcon, TagIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal';
import ReportForm from '../../components/forms/ReportForm';

const BedDetailPage = () => {
  const { bedId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [bedData, setBedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  useEffect(() => {
    fetchBedDetails();
  }, [bedId]);

  const fetchBedDetails = async () => {
    try {
      setLoading(true);
      const response = await apiService.getBedPublic(bedId);
      
      if (response.success) {
        setBedData(response.data);
      } else {
        setError(response.message || 'Failed to fetch bed details');
        toast.error(response.message || 'Failed to fetch bed details');
      }
    } catch (err) {
      const errorMessage = err.message || 'An error occurred while fetching bed details';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleReportClick = () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    setShowReportModal(true);
  };

  const handleReportSuccess = () => {
    setShowReportModal(false);
    toast.success('Thank you for your report. It has been submitted for review.');
  };

  const handleLoginRedirect = () => {
    setShowLoginPrompt(false);
    // Store the current URL to redirect back after login
    localStorage.setItem('redirectAfterLogin', window.location.pathname);
    navigate('/login');
  };

  const InfoCard = ({ title, children, icon: Icon }) => (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
      <div className="flex items-center mb-4">
        {Icon && <Icon className="h-6 w-6 text-green-600 mr-2" />}
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      </div>
      {children}
    </div>
  );

  const InfoRow = ({ label, value, icon: Icon }) => (
    <div className="flex items-start py-2">
      {Icon && <Icon className="h-5 w-5 text-gray-500 mr-3 mt-0.5 flex-shrink-0" />}
      <div className="flex-1">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className="text-sm text-gray-900 mt-1">{value || 'N/A'}</dd>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading bed details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Bed Details</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/beds')}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
          >
            Back to Beds
          </button>
        </div>
      </div>
    );
  }

  if (!bedData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Bed Not Found</h2>
          <p className="text-gray-600 mb-4">The requested bed could not be found.</p>
          <button
            onClick={() => navigate('/beds')}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
          >
            Back to Beds
          </button>
        </div>
      </div>
    );
  }

  const { bed, farm, plot } = bedData;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <nav className="flex mb-4" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-sm">
              <li>
                <button
                  onClick={() => navigate(-1)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Back
                </button>
              </li>
              <li className="text-gray-400">/</li>
              <li className="text-gray-900 font-medium">Bed Details</li>
            </ol>
          </nav>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {bed.crop} Bed {bed.bed_number && `#${bed.bed_number}`}
              </h1>
              <p className="text-gray-600 mt-1">
                Detailed information about bed {bed.id}
              </p>
            </div>

            <div className="flex items-center space-x-4">
              {/* Report Button */}
              <button
                onClick={handleReportClick}
                className="inline-flex items-center px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700 transition-colors shadow-sm"
              >
                <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                Update
              </button>

              {bed.thumbnail && (
                <div className="hidden sm:block">
                  <img
                    src={bed.thumbnail}
                    alt={`${bed.crop} bed`}
                    className="h-32 w-32 rounded-lg object-contain border-2 border-gray-200"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Bed Information */}
            <InfoCard title="Bed Information" icon={BeakerIcon}>
              <dl className="divide-y divide-gray-100">
                <InfoRow label="Bed Number" value={bed.bed_number} icon={TagIcon} />
                <InfoRow label="Crop Type" value={bed.crop} icon={TagIcon} />
                <InfoRow label="Bed Size" value={`${bed.size} sq ft`} icon={MapPinIcon} />
                <InfoRow label="Seed Type" value={bed.seed} />
                <InfoRow label="Seed Source" value={bed.source} />
                <InfoRow label="Planting Date" value={formatDate(bed.date)} icon={CalendarIcon} />
                <InfoRow label="Created" value={formatDate(bed.created_at)} />
                <InfoRow label="Last Updated" value={formatDate(bed.updated_at)} />
              </dl>
              
              {bed.thumbnail && (
                <div className="mt-4 sm:hidden">
                  <img
                    src={bed.thumbnail}
                    alt={`${bed.crop} bed`}
                    className="w-full h-64 rounded-lg object-contain border border-gray-200"
                  />
                </div>
              )}
            </InfoCard>

            {/* Plot Information */}
            <InfoCard title="Plot Information" icon={MapPinIcon}>
              <dl className="divide-y divide-gray-100">
                <InfoRow label="Plot Name" value={plot.name} />
                <InfoRow label="Description" value={plot.description} />
                <InfoRow label="Area" value={`${plot.area} acres`} />
                <InfoRow 
                  label="Coordinates" 
                  value={plot.coordinates ? 
                    `${plot.coordinates.latitude}°N, ${plot.coordinates.longitude}°E` : 
                    'N/A'
                  } 
                />
                <InfoRow label="Created" value={formatDate(plot.created_at)} />
              </dl>
            </InfoCard>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Farm Information */}
            <InfoCard title="Farm Information" icon={BuildingOfficeIcon}>
              <dl className="divide-y divide-gray-100">
                <InfoRow label="Farm Name" value={farm.name} />
                <InfoRow label="Description" value={farm.description} />
                <InfoRow label="Owner" value={farm.owner} icon={UserIcon} />
                <InfoRow label="Contact" value={farm.contact} icon={PhoneIcon} />
                <InfoRow label="Total Area" value={`${farm.area} acres`} />
                <InfoRow label="Location" value={`${farm.address}, ${farm.region}, ${farm.country}`} />
                <InfoRow 
                  label="Coordinates" 
                  value={farm.coordinates && (farm.coordinates.latitude || farm.coordinates.longitude) ? 
                    `${farm.coordinates.latitude}°N, ${farm.coordinates.longitude}°E` : 
                    'N/A'
                  } 
                />
                <InfoRow label="Established" value={formatDate(farm.created_at)} />
              </dl>
            </InfoCard>

            {/* QR Code Section */}
            {bed.qr_code && (
              <InfoCard title="QR Code">
                <div className="text-center">
                  <img
                    src={bed.qr_code}
                    alt="Bed QR Code"
                    className="mx-auto h-32 w-32 border border-gray-200 rounded"
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    Scan to view this bed's details
                  </p>
                </div>
              </InfoCard>
            )}
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/beds')}
            className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-colors font-medium"
          >
            ← Back to Beds
          </button>
        </div>
      </div>

      {/* Report Modal */}
      <Modal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Report an Issue"
        size="xl"
      >
        <ReportForm
          bedData={bedData}
          onSuccess={handleReportSuccess}
          onCancel={() => setShowReportModal(false)}
        />
      </Modal>

      {/* Login Prompt Modal */}
      <Modal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title="Login Required"
        size="md"
        footer={
          <>
            <button
              onClick={() => setShowLoginPrompt(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleLoginRedirect}
              className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
            >
              Login to Continue
            </button>
          </>
        }
      >
        <div className="text-center py-4">
          <ExclamationTriangleIcon className="h-12 w-12 text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">
            You need to be logged in to report issues. Please log in to continue.
          </p>
          <p className="text-sm text-gray-500">
            After logging in, you'll be redirected back to this page to submit your report.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default BedDetailPage;